import React, { useCallback, useMemo } from 'react';

import { CSSProp, CSSProperties } from 'styled-components';

import { Box } from '@adsk/alloy-react-box';
import Checkbox, { CheckboxState } from '@adsk/alloy-react-checkbox';
import Divider from '@adsk/alloy-react-divider';
import { useLocale } from '@adsk/alloy-react-locale';
import {
  TableConfigurationColumns,
  TableConfigurationMenu,
  TableConfigurationReset,
  TableExpanderButton,
} from '@adsk/alloy-react-table-v2';
import { STICKY_TYPES } from '@adsk/alloy-react-table-v2/es/consts';
import { TableConfigurationMenuProps } from '@adsk/alloy-react-table-v2/es/TableConfiguration';
import theme from '@adsk/alloy-react-theme';
import { OverflowTooltip } from '@adsk/alloy-react-tooltip';

import { ActivityNameCell } from './ActivityNameCell';
import { dateFormatter, formatFloatProgress } from './formatter';
import { formatUniqueId } from '../../../helpers';
import { ActivityCode, ActivityRow } from '../../types';
import { useScheduleContext } from '../ScheduleProvider';
import { ACTIONS } from '../ScheduleProvider/ScheduleProvider';

const useActivityCodeColumns = (activityCodes: string[]) => {
  return activityCodes.map((name: string) => ({
    id: name,
    label: name,
    renderHeader: () => <OverflowTooltip>{name}</OverflowTooltip>,
    width: 70,
    show: false,
    accessor: 'activity.activityCodes',
    renderCell: ({ value }: { value: ActivityCode[] }) => (
      <OverflowTooltip>
        {value?.find((activityCode) => activityCode.name === name)?.value ??
          '-'}
      </OverflowTooltip>
    ),
  }));
};

const DurationCell = ({ value }: { value: number | string | undefined }) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const text =
    typeof value === 'number' ? `${value} ${T.translate('DAYS')}` : '-';
  return <OverflowTooltip>{text}</OverflowTooltip>;
};

export const useScheduleActivityColumns = (
  activityCodes: string[],
  toggleRowSelected: (id: string, checked: boolean) => void,
) => {
  const { T } = useLocale(({ locale }) => require(`../../i18n/${locale}.json`));
  const activityCodeColumns = useActivityCodeColumns(activityCodes);
  const { dispatch } = useScheduleContext();

  const onExpandRow = useCallback(
    (id: string, expand: boolean) => {
      dispatch({
        type: ACTIONS.UPDATE_COLLAPSED_ROWS,
        payload: {
          key: id,
          value: !expand,
        },
      });
    },
    [dispatch],
  );

  const baseColumns = useMemo(
    () => [
      {
        id: 'checkbox',
        accessor: 'id',
        renderHeader: () => '',
        width: 32,
        maxWidth: 32,
        minWidth: 32,
        sticky: STICKY_TYPES.LEFT,
        renderCell: ({
          value: id,
          row,
        }: {
          value: string;
          row: { isSelected: boolean; original: ActivityRow };
        }) => (
          <Checkbox
            checked={row.isSelected}
            disabled={row.original.disabled}
            onChange={(state: CheckboxState) =>
              toggleRowSelected(id, state as boolean)
            }
          />
        ),
      },
      {
        id: 'id',
        width: 40,
        minWidth: 40,
        label: T.translate('COLUMNS.ID')?.toString(),
        renderHeader: () => (
          <Box
            width="100%"
            display="grid"
            gridTemplateColumns="16px 1fr"
            gridAutoFlow="column"
            marginLeft="auto"
          >
            <Box gridColumn="2">{T.translate('COLUMNS.ID')}</Box>
          </Box>
        ),
        accessor: 'activity.sequentialId',
        renderCell: ({
          value,
          row,
        }: {
          value: number;
          row: { original: ActivityRow };
        }) => (
          <Box
            display="grid"
            gridTemplateColumns="3px 1fr"
            gridAutoFlow="column"
            alignItems="center"
            style={{
              columnGap: '13px',
            }}
          >
            {row.original.activity.isCriticalPath && (
              <Box
                width="3px"
                height="16px"
                borderRadius="4px"
                backgroundColor="red500"
              />
            )}
            <Box gridColumn="2">{formatUniqueId(value)}</Box>
          </Box>
        ),
      },
      {
        id: 'fileActivityId',
        accessor: 'activity.fileActivityId',
        width: 75,
        minWidth: 75,
        label: T.translate('COLUMNS.FILE_ACTIVITY_ID')?.toString(),
        renderCell: ({ value }: { value: string }) => (
          <OverflowTooltip>{value}</OverflowTooltip>
        ),
        renderHeader: () => (
          <OverflowTooltip>
            {T.translate('COLUMNS.FILE_ACTIVITY_ID')}
          </OverflowTooltip>
        ),
      },
      {
        id: 'name',
        accessor: 'activity.name',
        width: 215,
        renderHeader: () => (
          <OverflowTooltip>
            {T.translate('COLUMNS.ACTIVITY_NAME_COLUMN')}
          </OverflowTooltip>
        ),
        renderCell: ({
          row,
        }: {
          row: {
            depth: number;
            original: ActivityRow;
            canExpand: boolean;
            isExpanded: boolean;
            isSelected: boolean;
            getToggleRowExpandedProps: (arg0: { style?: CSSProperties }) => {
              style?: CSSProp | undefined;
            };
          };
        }) => (
          <Box
            display="grid"
            gridTemplateColumns="20px 1fr"
            alignItems="center"
            marginLeft={row.depth * 20}
            style={{ columnGap: '8px' }}
          >
            {row.canExpand &&
              (() => {
                const expandProps = row.getToggleRowExpandedProps({
                  style: {},
                });
                return (
                  <TableExpanderButton
                    {...expandProps}
                    style={expandProps.style}
                    isExpanded={row.original.expanded}
                    onClick={() => {
                      onExpandRow(row.original.id, !row.original.expanded);
                    }}
                  />
                );
              })()}
            <ActivityNameCell
              canExpand={row.canExpand}
              activity={row.original.activity}
              onClick={() =>
                toggleRowSelected(row.original.id, !row.isSelected)
              }
            />
          </Box>
        ),
      },
      {
        id: 'startDate',
        label: T.translate('COLUMNS.START')?.toString(),
        renderHeader: () => T.translate('COLUMNS.START'),
        width: 90,
        accessor: 'activity.startDateToDisplay',
        renderCell: ({ row }: { row: { original: ActivityRow } }) => (
          <OverflowTooltip>
            {dateFormatter({
              date: row.original.activity.startDateToDisplay,
              isActual: !!row.original.activity.actualStart,
            })}
          </OverflowTooltip>
        ),
      },
      {
        id: 'endDate',
        accessor: 'activity.endDateToDisplay',
        label: T.translate('COLUMNS.FINISH')?.toString(),
        renderHeader: () => (
          <OverflowTooltip>{T.translate('COLUMNS.FINISH')}</OverflowTooltip>
        ),
        width: 90,
        renderCell: ({ row }: { row: { original: ActivityRow } }) => (
          <OverflowTooltip>
            {dateFormatter({
              date: row.original.activity.endDateToDisplay,
              isActual: !!row.original.activity.actualFinish,
            })}
          </OverflowTooltip>
        ),
      },
      {
        id: 'progress',
        label: T.translate('COLUMNS.PROGRESS')?.toString(),
        renderHeader: () => (
          <OverflowTooltip>{T.translate('COLUMNS.PROGRESS')}</OverflowTooltip>
        ),
        width: 90,
        accessor: 'activity.completionPercentage',
        renderCell: ({ value }: { value: number }) => (
          <OverflowTooltip>{`${formatFloatProgress(value)}%`}</OverflowTooltip>
        ),
      },
      {
        id: 'duration',
        label: T.translate('COLUMNS.DURATION')?.toString(),
        renderHeader: () => (
          <OverflowTooltip>{T.translate('COLUMNS.DURATION')}</OverflowTooltip>
        ),
        width: 90,
        accessor: 'activity.durationDays',
        renderCell: ({ value }: { value: number | undefined }) => (
          <DurationCell value={value} />
        ),
      },
      {
        id: 'remainingDuration',
        label: T.translate('COLUMNS.REMAINING_DURATION')?.toString(),
        renderHeader: () => (
          <OverflowTooltip>
            {T.translate('COLUMNS.REMAINING_DURATION')}
          </OverflowTooltip>
        ),
        width: 135,
        accessor: 'activity.remainingDurationDays',
        renderCell: ({ value }: { value: number | undefined }) => (
          <DurationCell value={value} />
        ),
      },
      {
        id: 'resources',
        label: T.translate('COLUMNS.RESOURCES')?.toString(),
        renderHeader: () => (
          <OverflowTooltip>{T.translate('COLUMNS.RESOURCES')}</OverflowTooltip>
        ),
        width: 150,
        accessor: 'activity.assignees',
        renderCell: ({ value }: { value: { name: string }[] }) => (
          <OverflowTooltip>
            {value.length > 0
              ? value.map(({ name }: { name: string }) => name).join(', ')
              : '-'}
          </OverflowTooltip>
        ),
      },
    ],
    [T, onExpandRow, toggleRowSelected],
  );

  const settingsColumn = useMemo(
    () => ({
      id: 'settings',
      sticky: STICKY_TYPES.RIGHT,
      width: 20,
      renderHeader: (table: TableConfigurationMenuProps<ActivityRow>) => (
        <TableConfigurationMenu
          {...table}
          renderMenuContent={({
            handleToggleColumn,
            isColumnReorderable,
            handleOnDragEnd,
            validColumns,
            resetLabel,
            reset,
          }) => (
            <div style={{ minWidth: 280, paddingTop: '8px' }}>
              <TableConfigurationColumns
                handleToggleColumn={handleToggleColumn}
                handleOnDragEnd={handleOnDragEnd}
                validColumns={validColumns}
                isColumnReorderable={isColumnReorderable}
              />
              <Divider color={theme.colors.charcoal200} />

              <TableConfigurationReset label={resetLabel} reset={reset} />
            </div>
          )}
        />
      ),
    }),
    [],
  );

  return [...baseColumns, ...activityCodeColumns, settingsColumn];
};
